import { useEffect, useState } from "react";
import { LogIn, Code2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import api from "../lib/api";

export default function Login({ onDevLogin }) {
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState({
    allowed_domains: ["vetcomer.com"],
    dev_login_enabled: false,
  });

  useEffect(() => {
    // Carga configuración pública del backend (dominios permitidos + flag dev)
    api.get("/auth/config")
      .then((r) => setAuthConfig(r.data))
      .catch(() => { /* defaults are fine */ });

    // Si el redirect de Google trae error de dominio en query string, mostrar toast
    const url = new URL(window.location.href);
    const err = url.searchParams.get("auth_error");
    if (err) {
      toast.error(decodeURIComponent(err), { duration: 8000 });
      url.searchParams.delete("auth_error");
      window.history.replaceState(null, "", url.pathname + url.hash);
    }
  }, []);

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/login", { redirect_uri: redirectUri });
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar el login con Google");
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/dev-login");
      localStorage.setItem("vetcomer_token", data.token);
      if (onDevLogin) onDevLogin(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sin autenticacion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)",
      }}
      data-testid="login-page"
    >
      {/* Decorative shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-zinc-200/40 blur-3xl" />
        <div className="absolute -bottom-48 -right-48 w-[500px] h-[500px] rounded-full bg-zinc-300/30 blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-zinc-100/50 blur-2xl" />
      </div>

      <Card className="relative z-10 w-full max-w-sm bg-white/95 backdrop-blur-sm border-zinc-200 shadow-2xl shadow-zinc-900/5 rounded-2xl">
        <CardContent className="p-8 sm:p-10">
          {/* Marca DADAO (sin logo, solo texto) */}
          <div className="text-center mb-8">
            <img
              src="/dadao-logo.png"
              alt="DADAO · VETCOMER WORKFORCE SYSTEM"
              className="w-full max-w-[260px] mx-auto h-auto"
              data-testid="login-logo"
            />
          </div>

          <p className="text-center text-sm text-zinc-500 mb-3">
            Iniciar sesion
          </p>

          {/* Acceso restringido a usuarios autorizados */}
          <div
            className="flex items-start gap-2 mb-5 px-3 py-2.5 rounded-lg bg-zinc-50 border border-zinc-200"
            data-testid="login-domain-hint"
          >
            <ShieldCheck className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-[11px] text-zinc-600 leading-snug">
              Acceso únicamente para usuarios autorizados por el administrador.
              Si tu correo no está habilitado, el ingreso será rechazado.
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full h-12 text-sm font-medium border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 transition-all duration-200 rounded-xl"
              onClick={handleGoogleLogin}
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Iniciar sesion con Google
            </Button>

            {authConfig.dev_login_enabled && (
              <>
                <div className="relative my-4">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-zinc-400">
                    o
                  </span>
                </div>

                <Button
                  variant="outline"
                  className="w-full h-12 text-sm font-medium border-dashed border-zinc-300 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700 hover:border-zinc-400 transition-all duration-200 rounded-xl"
                  onClick={handleDevLogin}
                  disabled={loading}
                  data-testid="dev-login-button"
                >
                  <Code2 className="w-4 h-4 mr-3" />
                  {loading ? "Ingresando..." : "Sin autenticacion (desarrollo)"}
                </Button>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="mt-10 text-center">
            <p className="text-[11px] text-zinc-400">
              2026 VETCOMER LTDA &middot; Costa Rica
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
